/* eslint-disable @typescript-eslint/restrict-plus-operands */
import {
  type WorkOrderIssue,
  type FormErrors,
  type WorkOrderFormValues,
} from 'types/Squares/WorkOrderManagement/work.order.creation.form.types';
import {
  fieldMetadata,
  issueCreationFields,
  issueAssignmentFields,
  issueCompletionFields,
} from '../components/meta/metadata';

/**
 * Validates the fields for the 'creation' step.
 * @param values - The entire form's current values.
 * @returns An object containing error messages for invalid fields.
 */
export const validateCreationStep = (
  values: WorkOrderFormValues
): FormErrors => {
  const errors: FormErrors = {};
  const hasComponents = values.components && values.components.length > 0;

  for (const key in fieldMetadata) {
    const meta = fieldMetadata[key];
    if (meta.step === 'creation' && meta.required) {
      if (hasComponents && issueCreationFields.includes(key)) {
        continue;
      }

      const value = values[key];
      if (value === '' || value == null) {
        errors[key] = `${meta.label} is required.`;
      }
    }
  }

  if (hasComponents) {
    const componentErrors: Array<Record<string, string>> = [];
    let hasIssueErrors = false;

    values.components.forEach((issue: any, index: number) => {
      const componentItemError: Record<string, string> = {};

      issueCreationFields.forEach((fieldKey) => {
        const meta = fieldMetadata[fieldKey];
        if (meta?.required) {
          const value = issue[fieldKey as keyof typeof issue];
          if (value === '' || value == null) {
            componentItemError[
              fieldKey
            ] = `${meta.label} is required for issue`;
          }
        }
      });

      if (issue.plannedStartDate && issue.plannedEndDate) {
        const start = new Date(issue.plannedStartDate).getTime();
        const end = new Date(issue.plannedEndDate).getTime();

        if (!isNaN(start) && !isNaN(end)) {
          if (start > end) {
            componentItemError.plannedStartDate = `Planned Start Date cannot be after Planned End Date.`;
          }
        }
      }
      if (Object.keys(componentItemError).length > 0) {
        componentErrors[index] = componentItemError;
        hasIssueErrors = true;
      }
    });

    if (hasIssueErrors) {
      (errors as any).components = componentErrors;
    }
  }

  return errors;
};

/**
 * Validates the fields for the 'assignment' step.
 * @param values - The entire form's current values.
 * @returns An object containing error messages for invalid fields.
 */
export const validateAssignmentStep = (
  values: WorkOrderFormValues
): FormErrors => {
  const errors: FormErrors = {};
  const hasIssues = values.components && values.components.length > 0;

  for (const key in fieldMetadata) {
    const meta = fieldMetadata[key];
    if (meta.step === 'assignment' && meta.required) {
      if (hasIssues && issueAssignmentFields.includes(key)) {
        continue;
      }

      const value = values[key];
      if (value === '' || value == null) {
        errors[key] = `${meta.label} is required.`;
      }
    }
  }

  if (hasIssues) {
    const issuesErrors: Record<string, Record<string, string>> = {};
    let hasIssueErrors = false;

    values.components.forEach((issue: any, index: any) => {
      const issueErrors: Record<string, string> = {};

      issueAssignmentFields.forEach((fieldKey) => {
        const meta = fieldMetadata[fieldKey];
        if (meta?.required) {
          const value = issue[fieldKey as keyof typeof issue];
          if (value === '' || value == null) {
            issueErrors[fieldKey] = `${meta.label} is required for issue ${
              index + 1
            }.`;
          }
        }
      });

      if (Object.keys(issueErrors).length > 0) {
        issuesErrors[index] = issueErrors;
        hasIssueErrors = true;
      }
    });

    if (hasIssueErrors) {
      (errors as any).components = issuesErrors;
    }
  }

  return errors;
};

/**
 * Validates the fields for the 'completion' step.
 * @param values - The entire form's current values.
 * @returns An object containing error messages for invalid fields.
 */
export const validateCompletionStep = (
  values: WorkOrderFormValues
): FormErrors => {
  const errors: FormErrors = {};
  const hasComponents = values.components && values.components.length > 0;

  for (const key in fieldMetadata) {
    const meta = fieldMetadata[key];
    if (meta.step === 'completion' && meta.required) {
      if (hasComponents && issueCompletionFields.includes(key)) {
        continue;
      }

      const value = values[key];
      if (value === '' || value == null) {
        errors[key] = `${meta.label} is required.`;
      }

      // Extra validation for totalCost
      if (key === 'totalCost') {
        if (value === 0 || value === '0') {
          errors[key] = `${meta.label} cannot be zero.`;
        }
      }
    }
  }

  if (hasComponents) {
    const componentErrors: Array<Record<string, string>> = [];
    let hasIssueErrors = false;

    values.components.forEach((issue: WorkOrderIssue) => {
      const componentItemError: Record<string, string> = {};

      issueCompletionFields.forEach((fieldKey) => {
        const meta = fieldMetadata[fieldKey];
        if (meta?.required) {
          const value = issue[fieldKey as keyof typeof issue];
          if (value === '' || value == null) {
            componentItemError[
              fieldKey
            ] = `${meta.label} is required for issue`;
          }

          // If totalCost is also inside components (depends on your form design)
          if (fieldKey === 'totalCost') {
            if (value === 0 || value === '0') {
              componentItemError[fieldKey] = `${meta.label} cannot be zero.`;
            }
          }
        }
      });

      if (issue.actualStartDate && issue.actualEndDate) {
        const start = new Date(issue.actualStartDate).getTime();
        const end = new Date(issue.actualEndDate).getTime();

        if (!isNaN(start) && !isNaN(end)) {
          if (start >= end) {
            componentItemError.actualStartDate = `Actual End Date must be greater than Actual Start Date.`;
          }
        }
      }

      if (Object.keys(componentItemError).length > 0) {
        componentErrors.push(componentItemError);
        hasIssueErrors = true;
      }
    });

    if (hasIssueErrors) {
      (errors as any).components = componentErrors;
    }
  } else {
    if (values?.actualStartDate && values?.actualEndDate) {
      const start = new Date(values.actualStartDate).getTime();
      const end = new Date(values.actualEndDate).getTime();

      if (!isNaN(start) && !isNaN(end)) {
        if (start >= end) {
          errors.actualStartDate =
            'Actual End Date must be greater than Actual Start Date.';
        }
      }
    }
  }

  return errors;
};
